import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {EnrichedIssue, IssueDeferredItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {
  DeferredItem as PullDeferredItem,
  EnrichedPull,
} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'

type MixedPullDeferredItem = PullDeferredItem & {itemType: 'pull_request'}
type MainItem = {
  itemType: 'issue' | 'pull_request'
  id: number | bigint | string
  globalId?: string
}
type DeferredItem = IssueDeferredItem | MixedPullDeferredItem
type IssueEnrichment = Pick<
  EnrichedIssue,
  | 'isReadByCurrentUser'
  | 'linkedPullRequestCount'
  | 'linkedPullRequests'
  | 'subIssuesSummary'
  | 'blockedByCount'
  | 'reactionGroups'
>
type PullEnrichment = Pick<
  EnrichedPull,
  | 'reviewDecisionState'
  | 'statusCheckRollup'
  | 'approvedReviewsCount'
  | 'changesRequestedReviewsCount'
  | 'reactionGroups'
>
type EnrichedItem<T extends MainItem> = T extends {itemType: 'pull_request'} ? T & PullEnrichment : T & IssueEnrichment

function compositeKey(item: {itemType: string; id: number | bigint | string}): string {
  return `${item.itemType}:${item.id}`
}

export function enrichIssueItem<T extends MainItem>(item: T, deferred?: IssueDeferredItem): T & IssueEnrichment {
  return {
    ...item,
    isReadByCurrentUser: deferred?.isReadByCurrentUser,
    linkedPullRequestCount: deferred?.linkedPullRequestCount,
    linkedPullRequests: deferred?.linkedPullRequests,
    subIssuesSummary: deferred?.subIssuesSummary,
    blockedByCount: deferred?.blockedByCount,
    reactionGroups: deferred?.reactionGroups,
  }
}

export function enrichPullItem<T extends MainItem>(item: T, deferred?: PullDeferredItem): T & PullEnrichment {
  return {
    ...item,
    reviewDecisionState: deferred?.reviewDecisionState,
    statusCheckRollup: deferred?.statusCheckRollup,
    approvedReviewsCount: deferred?.approvedReviewsCount,
    changesRequestedReviewsCount: deferred?.changesRequestedReviewsCount,
    reactionGroups: deferred?.reactionGroups,
  }
}

export function mergeDeferredIssues<T extends MainItem>(main: T[], deferred: DeferredItem[]): Array<EnrichedItem<T>> {
  const deferredIndex = new Map<string, DeferredItem>()
  for (const item of deferred) {
    deferredIndex.set(compositeKey(item), item)
    deferredIndex.set(getListItemIdentity(item), item)
  }

  return main.map(item => {
    const enrichment = deferredIndex.get(getListItemIdentity(item)) ?? deferredIndex.get(compositeKey(item))
    if (item.itemType === 'pull_request') {
      return enrichPullItem(item, enrichment?.itemType === 'pull_request' ? enrichment : undefined) as EnrichedItem<T>
    }
    return enrichIssueItem(item, enrichment?.itemType === 'issue' ? enrichment : undefined) as EnrichedItem<T>
  })
}
